import { useRequest } from 'features/request/useRequest';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import type { IGetRowsParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo } from 'react';

import { getCatalogConstructionTypeItemsGql } from '../catalogConstructionTypesQueries';

interface IUseGetCatalogConstructionTypes {
    filterValues: Record<string, string[]>;
    id: string | undefined;
}

const getWhereVariables = (filterValues?: Record<string, string[]>) => {
    if (!filterValues) {
        return undefined;
    }

    return {
        and: Object.entries(filterValues).reduce((acc: any, [key, value]) => {
            if (value.length) {
                acc = [
                    ...acc,
                    {
                        [key]: { in: value },
                    },
                ];
            }

            return acc;
        }, []),
    };
};

export const useGetCatalogConstructionTypes = ({ filterValues, id }: IUseGetCatalogConstructionTypes) => {
    const [catalogConstructionTypesResponse, , , catalogConstructionTypesMakeRequest] = useRequest({
        requestId: 'catalogConstructionTypes',
        query: getCatalogConstructionTypeItemsGql,
        withEffect: false,
    });

    const variables = id
        ? {
              catalogUniqueId: id,
              where: getWhereVariables(filterValues),
          }
        : undefined;

    const changeOrderHandler = useCallback((params: IGetRowsParams) => {
        const { sortModel } = params;

        const { sort, colId } = sortModel?.[0] ?? {};

        if (colId && sort) {
            return [{ [colId]: sort.toUpperCase() }];
        }

        return undefined;
    }, []);

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'catalogConstructionTypes',
        requestFn: catalogConstructionTypesMakeRequest,
        requestVariables: variables,
        customSortFn: changeOrderHandler,
    });

    useEffect(() => {
        if (catalogConstructionTypesResponse?.catalogConstructionTypeItems?.items && getRowParams?.current) {
            getRowParams.current.successCallback(
                catalogConstructionTypesResponse.catalogConstructionTypeItems?.items ?? [],
                catalogConstructionTypesResponse.catalogConstructionTypeItems?.totalCount,
            );
        }
    }, [
        catalogConstructionTypesResponse?.catalogConstructionTypeItems?.items,
        catalogConstructionTypesResponse?.catalogConstructionTypeItems?.totalCount,
        filterValues,
        getRowParams,
    ]);

    const totalCount = catalogConstructionTypesResponse?.catalogConstructionTypeItems?.totalCount ?? 0;

    const fetchedCount = useMemo<number>(() => {
        if (!getRowParams?.current?.endRow || !totalCount) {
            return 0;
        }

        if (getRowParams.current.endRow > totalCount) {
            return totalCount;
        }

        return getRowParams.current.endRow;
    }, [getRowParams, totalCount]);

    return useMemo(
        () => ({
            datasource: dataSource,
            totalCount,
            fetchedCount,
            changeOrderHandler,
        }),
        [changeOrderHandler, dataSource, fetchedCount, totalCount],
    );
};
